import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RTTable from '../common/Table';
import RTSpinner from '../common/RTSpinner';
import tenantStorage from '../../utils/tenantStorage';
import RTButton from '../common/RTButton';
import RTFilters from '../common/RTFilters';
import { apiGet } from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import { formatDateToDDMMYYYY } from '../../utils/dateHelper';
import '../common/rtCommon.css';
import './JobReportList.css';
import RTPageLayout from '../common/RTPageLayout';

const JobReportList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState([]);

  const getDefaultDates = () => {
    const today = new Date();
    const firstDayCurrentMonth = new Date(
      today.getFullYear(),
      today.getMonth(),
      1,
    );
    return {
      start_date: firstDayCurrentMonth,
      end_date: today,
    };
  };

  const getDefaultFilters = () => ({
    start_date: getDefaultDates().start_date,
    end_date: getDefaultDates().end_date,
    job_name: '',
    job_type: '',
    status: '',
  });

  const [filters, setFilters] = useState(getDefaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(getDefaultFilters);

  const filterFields = useMemo(
    () => [
      {
        key: 'start_date',
        label: 'Start Date',
        type: 'date',
        width: 'compact',
      },
      {
        key: 'end_date',
        label: 'End Date',
        type: 'date',
        width: 'compact',
      },
      {
        key: 'job_name',
        label: 'Job Name',
        type: 'text',
        width: 'medium',
        placeholder: 'Search job name...',
      },
      {
        key: 'job_type',
        label: 'Job Type',
        type: 'dropdown',
        width: 'compact',
        options: [
          { value: '', label: 'All Types' },
          { value: 'send_invoice', label: 'Send Invoice' },
          { value: 'send_template_message', label: 'Send Template Message' },
        ],
      },
      {
        key: 'status',
        label: 'Status',
        type: 'dropdown',
        width: 'compact',
        options: [
          { value: '', label: 'All Status' },
          { value: 'pending', label: 'Pending' },
          { value: 'processing', label: 'Processing' },
          { value: 'completed', label: 'Completed' },
          { value: 'failed', label: 'Failed' },
        ],
      },
    ],
    [],
  );

  const fetchJobs = async (
    filterParams = appliedFilters,
    abortController = null,
  ) => {
    setLoading(true);
    try {
      const params = {};
      if (filterParams.status) params.status = filterParams.status;
      if (filterParams.job_name) params.job_name = filterParams.job_name;
      if (filterParams.job_type) params.job_type = filterParams.job_type;

      // Format dates to YYYY-MM-DD in local timezone
      if (filterParams.start_date) {
        const year = filterParams.start_date.getFullYear();
        const month = String(filterParams.start_date.getMonth() + 1).padStart(
          2,
          '0',
        );
        const day = String(filterParams.start_date.getDate()).padStart(2, '0');
        params.start_date = `${year}-${month}-${day}`;
      }
      if (filterParams.end_date) {
        const year = filterParams.end_date.getFullYear();
        const month = String(filterParams.end_date.getMonth() + 1).padStart(
          2,
          '0',
        );
        const day = String(filterParams.end_date.getDate()).padStart(2, '0');
        params.end_date = `${year}-${month}-${day}`;
      }

      console.log('Applied filters:', filterParams);
      console.log('API params:', params);

      const token = tenantStorage.getItem('token');
      const response = await apiGet('/jobs/', {
        params: params,
        headers: { Authorization: `Bearer ${token}` },
        signal: abortController?.signal,
      });

      const apiResponse = response.data;
      if (apiResponse && Array.isArray(apiResponse)) {
        setJobs(apiResponse);
        toastSuccess(`Loaded ${apiResponse.length} job records`);
      } else {
        setJobs([]);
        toastError('No jobs found for the selected filters.');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error loading jobs:', err);
        toastError('Failed to fetch jobs');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs(appliedFilters);

    // eslint-disable-next-line
  }, []);

  const formatJobType = (jobType) => {
    if (!jobType) return '';
    return jobType
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const handleFilterChange = (key, value, { applyImmediately } = {}) => {
    const next = {
      ...filters,
      [key]:
        value instanceof Date || value === null || value === undefined
          ? value
          : (value ?? ''),
    };
    setFilters(next);
    if (applyImmediately) {
      setAppliedFilters(next);
      fetchJobs(next);
    }
  };

  const handleFilterApply = () => {
    setAppliedFilters(filters);
    fetchJobs(filters);
  };

  const handleClearFilters = () => {
    const defaultFilters = getDefaultFilters();
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    fetchJobs(defaultFilters);
  };

  const appliedFilterPills = useMemo(() => {
    const pills = [];
    if (appliedFilters.start_date) {
      pills.push({
        key: 'start_date',
        label: `Start: ${formatDateToDDMMYYYY(appliedFilters.start_date)}`,
      });
    }
    if (appliedFilters.end_date) {
      pills.push({
        key: 'end_date',
        label: `End: ${formatDateToDDMMYYYY(appliedFilters.end_date)}`,
      });
    }
    if (appliedFilters.job_name) {
      pills.push({
        key: 'job_name',
        label: `Job Name: ${appliedFilters.job_name}`,
      });
    }
    if (appliedFilters.job_type) {
      pills.push({
        key: 'job_type',
        label: `Job Type: ${formatJobType(appliedFilters.job_type)}`,
      });
    }
    if (appliedFilters.status) {
      pills.push({
        key: 'status',
        label: `Status: ${appliedFilters.status.charAt(0).toUpperCase()}${appliedFilters.status.slice(1)}`,
      });
    }
    return pills;
  }, [appliedFilters]);

  const handleRemoveAppliedFilter = (key) => {
    const defaultFilters = getDefaultFilters();
    const next = { ...appliedFilters };
    if (key === 'start_date') {
      next.start_date = defaultFilters.start_date;
    } else if (key === 'end_date') {
      next.end_date = defaultFilters.end_date;
    } else {
      next[key] = '';
    }
    setFilters(next);
    setAppliedFilters(next);
    fetchJobs(next);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatDuration = (start, end) => {
    if (!start || !end) return 'N/A';
    const startDate = new Date(start);
    const endDate = new Date(end);
    const durationMs = endDate - startDate;
    const durationMinutes = Math.floor(durationMs / 60000);
    const durationSeconds = Math.floor((durationMs % 60000) / 1000);
    return `${durationMinutes}m ${durationSeconds}s`;
  };

  const getStatusBadgeClass = (status) => {
    const baseClass = 'job-status-badge';

    if (status === 'completed') {
      return `${baseClass} job-status-completed`;
    } else if (status === 'failed') {
      return `${baseClass} job-status-failed`;
    } else if (status === 'processing') {
      return `${baseClass} job-status-processing`;
    } else {
      return `${baseClass} job-status-pending`;
    }
  };

  const viewJobDetails = (job) => {
    navigate(`/reports/jobs/${job.job_id}`);
  };

  const columns = [
    {
      Header: 'Job Name',
      accessor: 'job_name',
      Cell: ({ row }) => (
        <span
          className="job-name-link"
          onClick={() => viewJobDetails(row)}
          style={{ cursor: 'pointer' }}
        >
          {row.job_name}
        </span>
      ),
    },
    {
      Header: 'Job Type',
      accessor: 'job_type',
      Cell: ({ value }) => formatJobType(value),
    },
    {
      Header: 'Started At',
      accessor: 'job_start_time',
      Cell: ({ value }) => formatTimestamp(value),
    },
    {
      Header: 'Duration',
      accessor: 'duration',
      Cell: ({ row }) => formatDuration(row.job_start_time, row.job_end_time),
    },
    {
      Header: 'Total',
      accessor: 'total_records',
    },
    {
      Header: 'Processed',
      accessor: 'processed_records',
      Cell: ({ value, row }) => {
        if (row.status === 'processing') {
          const progress =
            row.total_records > 0
              ? Math.round((value / row.total_records) * 100)
              : 0;
          return `${value} (${progress}%)`;
        }
        return value;
      },
    },
    {
      Header: 'Success',
      accessor: 'success_records',
      Cell: ({ value, row }) => {
        const successRate =
          row.total_records > 0
            ? Math.round((value / row.total_records) * 100)
            : 0;
        return `${value} (${successRate}%)`;
      },
    },
    {
      Header: 'Failed',
      accessor: 'failed_records',
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ value }) => (
        <span className={getStatusBadgeClass(value)}>{value || 'Unknown'}</span>
      ),
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <RTButton type="link" size="sm" onClick={() => viewJobDetails(row)}>
          View Details
        </RTButton>
      ),
    },
  ];

  // Jobs list view
  return (
    <RTPageLayout activePath={location.pathname} title="Background Jobs">
      <div className="rt-section" style={{ flex: 'none' }}>
        <RTFilters
          fields={filterFields}
          values={filters}
          onChange={handleFilterChange}
          onApply={handleFilterApply}
          pills={appliedFilterPills}
          onRemovePill={handleRemoveAppliedFilter}
          onClearAll={handleClearFilters}
        />
      </div>

      {/* Table Section */}
      <div className="rt-section">
        {loading ? (
          <div className="job-spinner-container">
            <RTSpinner message="Loading jobs..." />
          </div>
        ) : (
          <RTTable
            columns={columns}
            data={jobs}
            exportable={false}
            emptyMessage="No jobs found for the selected criteria"
          />
        )}
      </div>
    </RTPageLayout>
  );
};

export default JobReportList;
